import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Wallet, Download, FileText, Save, User, Archive, CalendarRange } from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const todayIST = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return d.toISOString().slice(0, 10);
};

export default function Salary() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [users, setUsers] = useState([]);
  const [company, setCompany] = useState(null);
  const [selected, setSelected] = useState(null);
  const [month, setMonth] = useState(() => todayIST().slice(0, 7));
  const [preview, setPreview] = useState(null);
  const [manualDays, setManualDays] = useState("");
  const [loading, setLoading] = useState(false);

  async function loadUsers() {
    if (isAdmin) {
      const { data } = await api.get("/users");
      setUsers(data);
      if (!selected && data.length > 0) setSelected(data[0]);
    } else if (me) {
      // Staff can only see themselves
      setUsers([me]);
      setSelected(me);
    }
  }
  async function loadCompany() {
    const { data } = await api.get("/salary/company"); setCompany(data);
  }
  useEffect(() => { loadUsers(); loadCompany(); /* eslint-disable-next-line */ }, [me?.id]);

  async function loadPreview() {
    if (!selected) return;
    setLoading(true);
    try {
      const params = { user_id: selected.id, month };
      if (manualDays !== "" && !isNaN(Number(manualDays))) params.days_present = Number(manualDays);
      const { data } = await api.get("/salary/preview", { params });
      setPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { loadPreview(); /* eslint-disable-next-line */ }, [selected?.id, month, manualDays]);

  async function download(kind) {
    if (!selected) return;
    const url = `/salary/slip.${kind}`;
    const params = { user_id: selected.id, month };
    if (manualDays !== "" && !isNaN(Number(manualDays))) params.days_present = Number(manualDays);
    try {
      const res = await api.get(url, { params, responseType: "blob" });
      const filename = `closet-salary-${selected.name.replace(/\s+/g, "_")}-${month}.${kind}`;
      const blob = new Blob([res.data]);
      const link = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = link; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(link), 800);
      toast.success(`${kind.toUpperCase()} slip downloaded`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  async function downloadBulkZip(kind) {
    try {
      toast.info("Building ZIP — this can take a few seconds…");
      const res = await api.get("/salary/bulk.zip", { params: { month, kind }, responseType: "blob" });
      const blob = new Blob([res.data]);
      const link = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = link; a.download = `closet-salaries-${month}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(link), 800);
      toast.success(`All slips (${kind.toUpperCase()}) downloaded as ZIP`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  async function downloadYearlyRegister() {
    const yr = Number(month.slice(0, 4));
    try {
      toast.info(`Building yearly register for ${yr}…`);
      const res = await api.get("/salary/yearly-register.xlsx", { params: { year: yr }, responseType: "blob" });
      const blob = new Blob([res.data]);
      const link = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = link; a.download = `closet-salary-register-${yr}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(link), 800);
      toast.success(`Yearly register ${yr} downloaded`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  const monthOptions = (() => {
    const now = new Date(todayIST()); const out = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      out.push({ key, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` });
    }
    return out;
  })();

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="salary-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">/ HR · PAYROLL</div>
          <h1 className="section-title text-5xl lg:text-6xl">Salary Slips.</h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-xl">One-click PDF or Excel salary slips. Days worked auto-pull from Attendance; override manually if needed.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 self-start">
          {isAdmin && <>
            <button onClick={() => downloadBulkZip("pdf")} className="brut-btn-outline px-4 py-2.5 inline-flex items-center gap-2" data-testid="bulk-zip-pdf-btn">
              <Archive size={14} /> All PDFs (ZIP)
            </button>
            <button onClick={() => downloadBulkZip("xlsx")} className="brut-btn-outline px-4 py-2.5 inline-flex items-center gap-2" data-testid="bulk-zip-xlsx-btn">
              <Archive size={14} /> All Excels (ZIP)
            </button>
            <button onClick={downloadYearlyRegister} className="brut-btn px-4 py-2.5 inline-flex items-center gap-2" data-testid="yearly-register-btn">
              <CalendarRange size={14} /> Yearly Register {month.slice(0, 4)}
            </button>
          </>}
        </div>
      </header>

      {company && (
        <div className="brut-card p-4 mb-5" data-testid="company-strip">
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">COMPANY</div>
          <div className="font-display font-black text-xl">{company.name} <span className="text-sm font-mono-ds text-zinc-500">· {company.tagline}</span></div>
          <div className="text-xs text-zinc-600 mt-1">{company.address}</div>
          <div className="text-xs text-zinc-600 mt-0.5">{company.phone} · {company.email}</div>
        </div>
      )}

      <div className={`grid grid-cols-1 ${isAdmin ? "lg:grid-cols-3" : "lg:grid-cols-2"} gap-5 mb-6`}>
        {/* Employee picker — admins only */}
        {isAdmin && (
          <div className="brut-card p-4">
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Employee</div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {users.map((u) => (
                <button key={u.id} onClick={() => setSelected(u)}
                  className={`w-full text-left px-3 py-2 border-2 border-black font-mono-ds text-xs ${selected?.id === u.id ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}
                  data-testid={`salary-emp-${u.id}`}>
                  <div className="font-display font-bold text-sm">{u.name}</div>
                  <div className={selected?.id === u.id ? "text-white/60" : "text-zinc-500"}>{u.designation || "—"} · CTC ₹{((u.salary_basic || 0) + (u.salary_hra || 0) + (u.salary_allowances || 0)).toLocaleString("en-IN")}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Month picker */}
        <div className="brut-card p-4">
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Month</div>
          <div className="grid grid-cols-3 gap-2">
            {monthOptions.map((m) => (
              <button key={m.key} onClick={() => setMonth(m.key)}
                className={`px-2 py-2 border-2 border-black font-mono-ds text-[10px] uppercase ${month === m.key ? "bg-[#7A3E1F] text-white" : "bg-white hover:bg-zinc-100"}`}
                data-testid={`salary-month-${m.key}`}>{m.label}</button>
            ))}
          </div>
        </div>

        {/* Manual override + Downloads */}
        <div className="brut-card p-4">
          {isAdmin && <>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Days worked (override)</div>
            <input type="number" step="0.5" className="brut-input font-mono-ds w-full mb-2" placeholder={preview ? `Auto: ${preview.days_present}` : ""} value={manualDays} onChange={(e) => setManualDays(e.target.value)} data-testid="salary-manual-days" />
            <div className="font-mono-ds text-[9px] text-zinc-500 mb-3">Blank = auto from Attendance</div>
          </>}
          <div className="flex flex-col gap-2">
            <button onClick={() => download("pdf")} disabled={!preview} className="brut-btn px-4 py-2 inline-flex items-center gap-2 disabled:opacity-50" data-testid="download-salary-pdf">
              <FileText size={14} /> Download PDF
            </button>
            <button onClick={() => download("xlsx")} disabled={!preview} className="brut-btn-outline px-4 py-2 inline-flex items-center gap-2 disabled:opacity-50" data-testid="download-salary-xlsx">
              <Download size={14} /> Download Excel
            </button>
            {isAdmin && selected && <button onClick={() => setSelected({ ...selected, __editing: true })} className="brut-btn-outline px-4 py-2 inline-flex items-center gap-2 mt-2" data-testid="edit-salary-config"><User size={14} /> Salary Settings</button>}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="brut-card p-10 text-center font-mono-ds text-xs uppercase text-zinc-500">Loading preview…</div>
      ) : preview ? (
        <div className="brut-card p-6" data-testid="salary-preview-card">
          <div className="flex flex-wrap gap-6 items-start mb-5 pb-4 border-b-2 border-black">
            <div className="flex-1">
              <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">EMPLOYEE</div>
              <div className="font-display font-black text-2xl">{preview.employee.name}</div>
              <div className="font-mono-ds text-xs text-zinc-500">{preview.employee.designation || "—"} · {preview.employee.email}</div>
            </div>
            <div>
              <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">MONTH</div>
              <div className="font-display font-black text-xl">{preview.month}</div>
            </div>
            <Stat label="Days in month" value={preview.total_days} />
            <Stat label="Present" value={preview.days_present} accent />
            <Stat label="Absent" value={preview.days_absent.toFixed(1)} />
            <Stat label="Late" value={preview.late_count} danger />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">EARNINGS</div>
              <table className="brut-table">
                <thead><tr><th>Component</th><th className="text-right">CTC (₹)</th><th className="text-right">Payable (₹)</th></tr></thead>
                <tbody>
                  <tr><td>Basic Pay</td><td className="text-right font-mono-ds">{preview.ctc_basic.toLocaleString("en-IN")}</td><td className="text-right font-mono-ds">{preview.payable_basic.toLocaleString("en-IN")}</td></tr>
                  <tr><td>HRA</td><td className="text-right font-mono-ds">{preview.ctc_hra.toLocaleString("en-IN")}</td><td className="text-right font-mono-ds">{preview.payable_hra.toLocaleString("en-IN")}</td></tr>
                  <tr><td>Allowances</td><td className="text-right font-mono-ds">{preview.ctc_allowances.toLocaleString("en-IN")}</td><td className="text-right font-mono-ds">{preview.payable_allowances.toLocaleString("en-IN")}</td></tr>
                </tbody>
                <tfoot>
                  <tr className="bg-black text-white font-display font-black"><td>Gross</td><td className="text-right font-mono-ds">{preview.ctc_gross.toLocaleString("en-IN")}</td><td className="text-right font-mono-ds">{preview.payable_gross.toLocaleString("en-IN")}</td></tr>
                </tfoot>
              </table>
            </div>
            <div>
              <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">DEDUCTIONS · NET</div>
              <div className="brut-card-flat p-4 bg-[#FDF6EE] mb-3">
                <div className="flex justify-between mb-1"><span>Total deductions</span><span className="font-mono-ds font-bold">₹{preview.deductions.toLocaleString("en-IN")}</span></div>
                <div className="font-mono-ds text-[10px] text-zinc-500">Configured under Salary Settings</div>
              </div>
              <div className="brut-card-flat p-5 bg-black text-white">
                <div className="font-mono-ds text-[10px] tracking-widest uppercase text-white/70">NET SALARY</div>
                <div className="font-display font-black text-4xl mt-1" data-testid="salary-net">₹{preview.net_salary.toLocaleString("en-IN")}</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="brut-card p-10 text-center font-mono-ds text-xs uppercase text-zinc-500">Pick an employee + month above</div>
      )}

      {selected?.__editing && (
        <SalaryConfigModal user={selected} onClose={() => setSelected({ ...selected, __editing: false })} onSaved={() => { setSelected({ ...selected, __editing: false }); loadUsers(); loadPreview(); }} />
      )}
    </div>
  );
}

function SalaryConfigModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    designation: user.designation || "",
    shift_in_time: user.shift_in_time || "10:30",
    shift_out_time: user.shift_out_time || "20:00",
    salary_basic: user.salary_basic || "",
    salary_hra: user.salary_hra || "",
    salary_allowances: user.salary_allowances || "",
    salary_deductions: user.salary_deductions || "",
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setBusy(true);
    try {
      const payload = {
        designation: form.designation,
        shift_in_time: form.shift_in_time,
        shift_out_time: form.shift_out_time,
        salary_basic: Number(form.salary_basic) || 0,
        salary_hra: Number(form.salary_hra) || 0,
        salary_allowances: Number(form.salary_allowances) || 0,
        salary_deductions: Number(form.salary_deductions) || 0,
      };
      await api.put(`/users/${user.id}`, payload);
      toast.success("Salary settings saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="salary-config-modal">
      <div className="brut-card bg-white w-full max-w-2xl">
        <div className="p-4 border-b-2 border-black flex items-center justify-between">
          <div className="font-display font-black text-xl">Salary Settings · {user.name}</div>
          <button onClick={onClose} className="p-1.5 border-2 border-black hover:bg-zinc-100">✕</button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Fld label="Designation"><input className="brut-input" value={form.designation} onChange={(e) => set("designation", e.target.value)} data-testid="cfg-designation" placeholder="e.g. Sales Executive" /></Fld>
          <Fld label="Shift In (HH:MM IST)"><input className="brut-input font-mono-ds" value={form.shift_in_time} onChange={(e) => set("shift_in_time", e.target.value)} data-testid="cfg-shift-in" placeholder="10:30" /></Fld>
          <Fld label="Shift Out (HH:MM IST)"><input className="brut-input font-mono-ds" value={form.shift_out_time} onChange={(e) => set("shift_out_time", e.target.value)} data-testid="cfg-shift-out" placeholder="20:00" /></Fld>
          <div />
          <Fld label="Basic (₹)"><input type="number" step="0.01" className="brut-input font-mono-ds" value={form.salary_basic} onChange={(e) => set("salary_basic", e.target.value)} data-testid="cfg-basic" /></Fld>
          <Fld label="HRA (₹)"><input type="number" step="0.01" className="brut-input font-mono-ds" value={form.salary_hra} onChange={(e) => set("salary_hra", e.target.value)} data-testid="cfg-hra" /></Fld>
          <Fld label="Allowances (₹)"><input type="number" step="0.01" className="brut-input font-mono-ds" value={form.salary_allowances} onChange={(e) => set("salary_allowances", e.target.value)} data-testid="cfg-allow" /></Fld>
          <Fld label="Deductions (₹) — fixed monthly"><input type="number" step="0.01" className="brut-input font-mono-ds" value={form.salary_deductions} onChange={(e) => set("salary_deductions", e.target.value)} data-testid="cfg-ded" /></Fld>
        </div>
        <div className="p-4 border-t-2 border-black flex justify-end gap-3">
          <button onClick={onClose} className="brut-btn-outline px-5 py-2">Cancel</button>
          <button onClick={save} disabled={busy} className="brut-btn px-5 py-2 inline-flex items-center gap-2" data-testid="save-salary-cfg"><Save size={14} /> {busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, danger }) {
  return (
    <div>
      <div className={`font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500`}>{label}</div>
      <div className={`font-display font-black text-2xl ${accent ? "text-[#22A06B]" : danger ? "text-[#FF3333]" : ""}`}>{value}</div>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
